import React from 'react';
import {
  Modal,
  Paper,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { X } from 'lucide-react';
import { extractName } from '../utils/hubsHelpers';

const HubPreviewModal = ({
  modalOpen,
  setModalOpen,
  pageSlug,
  category,
  state,
  town,
  heading,
  introLine,
  atmosphereDropdown,
  featuresDropdown,
  descriptionHtml,
  detailedContentHtml,
  matchingRetreats = [],
  pageName,
  placeName,
  relatedGroups,
  suitabilityItems = [],
  faqs = []
}) => {
  return (
    <Modal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 1, sm: 3 }
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 1040,
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #E2E8F0',
          backgroundColor: '#FFFFFF',
          outline: 'none'
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            p: '12px 18px',
            borderBottom: '1px solid #E2E8F0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#F8FAFC'
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1E293B' }}>
            Preview: how the public page will render
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ color: '#64748B', fontSize: 11 }}>
              healatindia.com{pageSlug}
            </Typography>
            <IconButton size="small" onClick={() => setModalOpen(false)} sx={{ color: '#64748B' }}>
              <X size={16} />
            </IconButton>
          </Box>
        </Box>

        {/* Modal Body - Public Page Layout */}
        <Box sx={{ overflowY: 'auto', flex: 1, backgroundColor: '#FFFFFF' }}>
          {/* Hero Section */}
          <Box sx={{ backgroundColor: '#EFE4CB', p: { xs: 3, md: 5 } }}>
            <Typography sx={{ color: '#64748B', fontSize: 12, mb: 1 }}>
              Home › {extractName(category)} › {extractName(state)} {town ? `› ${extractName(town)}` : ''}
            </Typography>
            <Typography variant="h3" sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 600, color: '#803927', mb: 1, fontFamily: 'Georgia, serif' }}>
              {heading || 'Best Retreats in Location'}
            </Typography>
            <Typography sx={{ fontSize: 16, color: '#475569', mb: 2 }}>
              {introLine}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['All Kerala', 'Top rated', 'Ayurveda', 'Doctor led'].map((tag) => (
                <Box
                  key={tag}
                  sx={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #D89F6A',
                    borderRadius: '999px',
                    px: 1.5,
                    py: 0.5,
                    fontSize: 12,
                    color: '#1E293B'
                  }}
                >
                  {tag}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Content & Facets Grid */}
          <Box
            sx={{
              p: { xs: 2.5, md: 4 },
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '220px 1fr' },
              gap: 3.5
            }}
          >
            {/* Facets Sidebar */}
            <Box sx={{ borderRight: { md: '1px solid #E2E8F0' }, pr: { md: 2.5 } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B', mb: 1 }}>
                Filters
              </Typography>
              {[
                {
                  l: 'Atmosphere',
                  items: (atmosphereDropdown?.options || []).map((o) => extractName(o)).filter(Boolean).slice(0, 5)
                },
                {
                  l: 'Features',
                  items: (featuresDropdown?.options || []).map((o) => extractName(o)).filter(Boolean).slice(0, 5)
                },
                {
                  l: 'Duration',
                  items: ['2 to 3 days', '4 to 5 days', '6 to 7 days', '8 to 14 days']
                }
              ].map((group) => {
                if (!group.items || group.items.length === 0) return null;
                return (
                  <Box key={group.l} sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, color: '#1E293B' }}>
                      {group.l}
                    </Typography>
                    {group.items.map((opt, oIdx) => (
                      <Box key={oIdx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25, fontSize: 12 }}>
                        <span>☐ {extractName(opt)}</span>
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>

            {/* Main Content Area */}
            <Box>
              <Box
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                sx={{ fontSize: 14, lineHeight: 1.6, mb: 2, color: '#334155' }}
              />

              <Typography variant="h5" sx={{ fontSize: 20, fontWeight: 600, color: '#803927', mb: 1.5 }}>
                {matchingRetreats.length} Best {extractName(pageName)} in {extractName(placeName)}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.75, mb: 3 }}>
                {matchingRetreats.length > 0 ? (
                  matchingRetreats.map((r) => (
                    <Paper
                      key={r.id}
                      elevation={0}
                      sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#FFFFFF' }}
                    >
                      <Box sx={{ height: 110, background: 'linear-gradient(135deg, #6E8964, #EFE4CB)' }} />
                      <Box sx={{ p: 1.5, fontSize: 12 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 12.5, mb: 0.5, color: '#1E293B' }}>
                          {extractName(r.t)}
                        </Typography>
                        <Typography sx={{ color: '#64748B', fontSize: 11 }}>
                          {extractName(r.town)}, {extractName(r.st)} · {r.d} days<br />
                          From INR {(r.p || 0).toLocaleString('en-IN')} · Free cancellation
                        </Typography>
                      </Box>
                    </Paper>
                  ))
                ) : (
                  <Typography sx={{ color: '#64748B', fontSize: 12 }}>No retreats match.</Typography>
                )}
              </Box>

              {/* Related Links */}
              <Box sx={{ my: 3 }}>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75, color: '#1E293B' }}>
                      Similar retreats
                    </Typography>
                    {relatedGroups?.similar?.length > 0 ? (
                      (relatedGroups?.similar || []).map((x) => (
                        <Box component="a" key={x.u} sx={{ display: 'block', color: '#803927', fontSize: 12.5, py: 0.25, textDecoration: 'underline' }}>
                          {x.t}
                        </Box>
                      ))
                    ) : (
                      <Typography sx={{ color: '#94A3B8', fontSize: 11 }}>none yet</Typography>
                    )}
                  </Box>

                  <Box sx={{ minWidth: 200 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75, color: '#1E293B' }}>
                      Nearby retreats
                    </Typography>
                    {relatedGroups?.nearby?.length > 0 ? (
                      (relatedGroups?.nearby || []).map((x) => (
                        <Box component="a" key={x.u} sx={{ display: 'block', color: '#803927', fontSize: 12.5, py: 0.25, textDecoration: 'underline' }}>
                          {x.t}
                        </Box>
                      ))
                    ) : (
                      <Typography sx={{ color: '#94A3B8', fontSize: 11 }}>none yet</Typography>
                    )}
                  </Box>

                  <Box sx={{ minWidth: 200 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75, color: '#1E293B' }}>
                      Explore retreats
                    </Typography>
                    {(relatedGroups?.explore || []).map((x) => (
                      <Box component="a" key={x.u} sx={{ display: 'block', color: '#803927', fontSize: 12.5, py: 0.25, textDecoration: 'underline' }}>
                        {x.t}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Suitability */}
              <Box sx={{ my: 3 }}>
                <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600, color: '#803927', mb: 1 }}>
                  This is for you if
                </Typography>
                <Box component="ul" sx={{ pl: 2.5, fontSize: 13.5, lineHeight: 1.6, m: 0, color: '#334155' }}>
                  {suitabilityItems.map((s, i) => (
                    <li key={i}>{extractName(s)}</li>
                  ))}
                </Box>
              </Box>

              {/* Detailed Content */}
              <Box
                dangerouslySetInnerHTML={{ __html: detailedContentHtml }}
                sx={{ my: 3, fontSize: 14, lineHeight: 1.65, color: '#334155' }}
              />

              {/* FAQs */}
              <Box sx={{ my: 3 }}>
                <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600, color: '#803927', mb: 1 }}>
                  Frequently asked questions
                </Typography>
                {faqs.map((f, i) => (
                  <Box key={i} component="details" sx={{ borderBottom: '1px solid #E2E8F0', py: 1, fontSize: 13.5 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#1E293B' }}>{extractName(f.question)}</summary>
                    <p style={{ margin: '8px 0 0', color: '#475569' }}>{extractName(f.answer)}</p>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default React.memo(HubPreviewModal);
